import { InboxInvoice } from "@finway-group/shared/lib/models"
import { type Employee } from "@finway-group/shared/lib/models/user/employee.model"
import axios from "axios"
import { type TFunction } from "i18next"
import { NotificationService } from "~Shared/services"
import { DialogService } from "~Shared/services/dialog.service"
import { NotificationDurationSec, NotificationTypeEnum } from "~Shared/services/notification.service"
import { store } from "~Shared/store"
import { convertUnencodedParamStringToObject } from "../config/axios.utils"

const MODEL = "inboxinvoices"

export const InboxInvoiceService = {
    updateOne: async (id: string, updatedInboxInvoice: InboxInvoice) => {
        const { data } = await axios.put(`/${MODEL}/${id}`, updatedInboxInvoice)
        return new InboxInvoice(data)
    },
    moveInboxInvoice: async (id: string, companyId: string) => {
        const { data } = await axios.patch(`/${MODEL}/${id}/move`, { companyId })
        return new InboxInvoice(data)
    },
    getInboxInvoices: () => store.getState().inboxInvoice.items,
    getInboxInvoiceIds: () => InboxInvoiceService.getInboxInvoices().map((ii: InboxInvoice) => ii.id),
    fetchInboxInvoicesCount: async (query = "") => {
        const {
            data: { count },
        } = await axios.get(`/${MODEL}/reports/counts`, { params: convertUnencodedParamStringToObject(query) })
        return count
    },
    getInboxInvoiceById: (id: string) => {
        const inboxInvoices = store.getState().inboxInvoices.items
        return inboxInvoices.find((ii: InboxInvoice) => ii.id === id)
    },
    fetchAllInboxInvoices: async (sortingCriteria: string, page: number = 1, limit: number = 1000) => {
        const params = convertUnencodedParamStringToObject(`page=${page}&limit=${limit}${sortingCriteria}`)
        const {
            data: { docs, totalPages, totalDocs },
        } = await axios.get(`/${MODEL}`, { params })

        return {
            inboxInvoices: docs.map((item: any) => new InboxInvoice(item)),
            totalDocs,
            totalPages,
        }
    },
    fetchInboxInvoices: async (queryParams: any, source: any, timeout?: number) => {
        const params = convertUnencodedParamStringToObject(
            `page=${queryParams.page}&limit=${queryParams.limit}${queryParams.filterQueryString}${queryParams.searchQueryString}${queryParams.sortQueryString}`,
        )
        const { data } = await axios.get(`/${MODEL}`, {
            cancelToken: source.token,
            timeout,
            params,
        })
        return data
    },
    fetchOneInboxInvoice: async (id: string) => {
        const { data } = await axios.get(`/${MODEL}/${id}`)
        return new InboxInvoice(data)
    },
    retryInboxInvoiceOcr: async (id: string) => {
        await axios.get(`/${MODEL}/retryOcr/${id}`)
    },
    createInboxInvoice: async (createInboxInvoice: InboxInvoice) => {
        const { data } = await axios.post(`/${MODEL}`, createInboxInvoice)
        return new InboxInvoice(data)
    },
    discardInboxInvoice: async (id: string) => axios.delete(`/${MODEL}/${id}`),
    deleteManyInboxInvoices: async (ids: Array<string>) => axios.post(`/${MODEL}/delete-inbox-invoices`, { ids }),
    fetchMultipleInvoicesByIds: async (ids: Array<string>) => {
        const invoicePromises = ids.map((invoiceId) => InboxInvoiceService.fetchOneInboxInvoice(invoiceId))
        const invoices = await Promise.all(invoicePromises)

        return invoices
    },

    assignInvoiceToUser: async (inboxInvoice: InboxInvoice, employee: Employee, t: TFunction) => {
        if (!(await DialogService.confirmAssignInvoiceToUser(employee.getFullName()))) {
            return
        }
        const newInvoice = new InboxInvoice({ ...inboxInvoice, assignedTo: employee.id })

        try {
            const data = await InboxInvoiceService.updateOne(inboxInvoice.id, newInvoice)
            NotificationService.send(
                NotificationTypeEnum.SUCCESS,
                t("notification:invoice_inbox.assign.title"),
                t("notification:invoice_inbox.assign.message", { name: data.fileName, employee: employee.getFullName() }),
                NotificationDurationSec.DEFAULT,
            )
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:invoice_inbox.assign.title"))
        }
    },
}
